// Section 2: Escrow Setup.
//
// Legacy model (an EscrowSetup row already exists for this deal): A1 sets up
// the escrow row (link + amount) by hand, issues escrow, then verifies
// funding — unchanged from the original manual ceremony. Mismatched funding
// amounts trigger a yellow warning — A1 may still proceed (no
// block) since FX / fee variance is expected.
//
// New model (no EscrowSetup row for this deal): Section 2 is a read-through
// view of the deal's real, API-driven Escrow.com transaction instead —
// link/amount/status are derived live from it rather than typed by hand.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Badge, Button, Card, FormField, Input } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { formatCurrency } from "@/components/deals/dealFormat";
import {
  useDealEscrowTransaction,
  useEscrowSetup,
  useIssueEscrow,
  useUpsertEscrowSetup,
  useVerifyEscrowFunding,
} from "@/hooks/dealFlow/useDealFlow";
import {
  useUndoEscrowFundingVerification,
  useUndoEscrowIssuance,
} from "@/hooks/dealFlow/useAdminUndo";
import { AdminUndoButton } from "../../admin/AdminUndoButton";
import { BypassBanner } from "../../gateBypass/BypassBanner";
import { BypassEscrowGateButton } from "../../gateBypass/BypassEscrowGateButton";
import { CreateEscrowModal } from "@/components/escrow/CreateEscrowModal";
import { isTerminalStage } from "@/components/deals/dealMenuRules";
import { EscrowLifecycleIndicator } from "@/components/escrow/EscrowLifecycleIndicator";
import {
  ESCROW_PERMISSIONS,
  ESCROW_STATUS_LABEL,
  escrowStatusBadge,
} from "@/types/escrow";
import type { DealDetailDto } from "@/types/deals";
import { calendarDateInputToIso } from "@/utils/datetime";

interface EscrowSetupSectionProps {
  deal: DealDetailDto;
}

export function EscrowSetupSection({ deal }: EscrowSetupSectionProps) {
  const row = useEscrowSetup(deal.id);

  if (row.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      </Card>
    );
  }

  // Closing-gate bypass — a force-completed gate leaves a real EscrowSetup row
  // with bypassedAt stamped (flipping the deal to the legacy view), so the
  // banner rides above whichever view renders. Prominent + distinct so no one
  // mistakes a QA-forced gate for a real, funded escrow close.
  const bypassBanner = row.data?.bypassedAt ? (
    <BypassBanner bypassedAt={row.data.bypassedAt} reason={row.data.bypassReason} />
  ) : null;

  // No legacy EscrowSetup row for this deal — Section 2 is backed by the
  // real, API-driven Escrow.com transaction instead of manual free-text entry.
  if (row.data == null) {
    return (
      <div className="flex flex-col gap-space-3">
        {bypassBanner}
        <EscrowSetupNewModelView deal={deal} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      {bypassBanner}
      <EscrowSetupLegacyView dealId={deal.id} />
    </div>
  );
}

function EscrowSetupLegacyView({ dealId }: { dealId: string }) {
  const row = useEscrowSetup(dealId);
  const upsert = useUpsertEscrowSetup(dealId);
  const issue = useIssueEscrow(dealId);
  const verify = useVerifyEscrowFunding(dealId);
  const undoIssuance = useUndoEscrowIssuance(dealId);
  const undoFunding = useUndoEscrowFundingVerification(dealId);
  const toast = useToast();

  const [link, setLink] = useState("");
  const [amount, setAmount] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [verifiedAt, setVerifiedAt] = useState("");
  const [verifiedAmount, setVerifiedAmount] = useState("");

  useEffect(() => {
    setLink(row.data?.escrowComLink ?? "");
    setAmount(row.data ? String(row.data.amountUsd) : "");
    setIssuedAt(toDateInputValue(row.data?.issuedToBuyerAt));
    setVerifiedAt(toDateInputValue(row.data?.fundingVerifiedAt));
    setVerifiedAmount(
      row.data?.fundingVerifiedAmount != null
        ? String(row.data.fundingVerifiedAmount)
        : "",
    );
  }, [row.data]);

  const surface =
    (verb: string) =>
    (err: unknown): void => {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : `Could not ${verb}.`,
      );
    };

  const onSaveSetup = async () => {
    try {
      await upsert.mutateAsync({
        escrowComLink: link,
        amountUsd: Number(amount) || 0,
      });
      toast.success("Escrow setup saved.");
    } catch (err) {
      surface("save escrow setup")(err);
    }
  };

  const onIssue = async () => {
    if (!issuedAt) return;
    try {
      await issue.mutateAsync({ issuedAt: calendarDateInputToIso(issuedAt)! });
      toast.success("Escrow issuance certified.");
    } catch (err) {
      surface("certify escrow issuance")(err);
    }
  };

  const onVerify = async () => {
    if (!verifiedAt || !verifiedAmount) return;
    try {
      await verify.mutateAsync({
        verifiedAt: new Date(verifiedAt).toISOString(),
        verifiedAmount: Number(verifiedAmount),
      });
      toast.success("Escrow funding verified.");
    } catch (err) {
      surface("certify funding")(err);
    }
  };

  const fundingMismatch =
    !!row.data?.amountUsd &&
    !!verifiedAmount &&
    Math.abs(Number(verifiedAmount) - row.data.amountUsd) > 0.01;

  return (
    <Card>
      <header className="mb-space-3">
        <h3 className="font-display text-h4 text-onyx m-0">
          Section 2 — Escrow Setup
        </h3>
        <p className="text-caption text-gray-mid m-0">
          A1 sets up escrow. B2 releases escrow in the Closing tab.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 mb-space-4">
        <FormField label="Escrow.com link" required>
          <Input
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://escrow.com/transaction/…"
          />
        </FormField>
        <FormField label="Amount (USD)" required>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="50000.00"
          />
        </FormField>
      </div>

      <div className="flex justify-end mb-space-4">
        <Button
          variant="secondary"
          onClick={() => void onSaveSetup()}
          disabled={!link.trim() || !amount || upsert.isPending}
        >
          {upsert.isPending ? "Saving…" : "Save escrow setup"}
        </Button>
      </div>

      <div className="border-t border-border-light pt-space-3 mt-space-2">
        <div className="flex items-center justify-between gap-space-2 mb-space-2">
          <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0">
            Issuance certification
          </h4>
          {row.data?.issuedToBuyerAt && (
            <AdminUndoButton
              label="Issuance"
              modalTitle="Undo Issuance Certification"
              modalConsequence={
                <>
                  This clears the &ldquo;Issued to buyer&rdquo; date and
                  re-enables the issuance inputs so A1 can re-certify. The
                  server refuses this undo if funding verification has already
                  been recorded — undo that first.
                </>
              }
              successToast="Escrow issuance undone."
              invoke={(reason) => undoIssuance.mutateAsync(reason)}
            />
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-space-3 items-end">
          <FormField label="Issued to buyer">
            <Input
              type="date"
              value={issuedAt}
              onChange={(e) => setIssuedAt(e.target.value)}
              disabled={!!row.data?.issuedToBuyerAt}
            />
          </FormField>
          <Button
            variant="primary"
            onClick={() => void onIssue()}
            disabled={!issuedAt || !!row.data?.issuedToBuyerAt || issue.isPending}
          >
            {row.data?.issuedToBuyerAt
              ? "Issued"
              : issue.isPending
                ? "Signing…"
                : "Certify issuance"}
          </Button>
        </div>
      </div>

      <div className="border-t border-border-light pt-space-3 mt-space-4">
        <div className="flex items-center justify-between gap-space-2 mb-space-2">
          <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0">
            Funding verification
          </h4>
          {row.data?.fundingVerifiedAt && (
            <AdminUndoButton
              label="Funding Verification"
              modalTitle="Undo Funding Verification"
              modalConsequence={
                <>
                  This clears the verified date, verified amount, and verifier
                  and re-enables the funding inputs so A1 can re-verify.
                  Issuance stays certified. The server refuses this undo once
                  escrow has been released in the Closing tab.
                </>
              }
              successToast="Escrow funding verification undone."
              invoke={(reason) => undoFunding.mutateAsync(reason)}
            />
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1.5fr_1fr] gap-space-3 items-end">
          <FormField label="Verified date">
            <Input
              type="date"
              value={verifiedAt}
              onChange={(e) => setVerifiedAt(e.target.value)}
              disabled={!!row.data?.fundingVerifiedAt}
            />
          </FormField>
          <FormField label="Verified amount (USD)">
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={verifiedAmount}
              onChange={(e) => setVerifiedAmount(e.target.value)}
              disabled={!!row.data?.fundingVerifiedAt}
            />
          </FormField>
          <Button
            variant="primary"
            onClick={() => void onVerify()}
            disabled={
              !verifiedAt ||
              !verifiedAmount ||
              !!row.data?.fundingVerifiedAt ||
              verify.isPending
            }
          >
            {row.data?.fundingVerifiedAt
              ? "Verified"
              : verify.isPending
                ? "Signing…"
                : "Certify funding"}
          </Button>
        </div>
        {fundingMismatch && (
          <div className="mt-space-2 flex items-start gap-space-2 bg-status-warning/10 border border-status-warning/30 rounded-md px-space-3 py-space-2">
            <AlertTriangle
              size={14}
              className="text-status-warning mt-[2px] shrink-0"
              aria-hidden
            />
            <p className="text-caption text-onyx m-0">
              Funded amount ({verifiedAmount}) differs from expected ({row.data?.amountUsd}). A1&apos;s discretion to proceed.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

function EscrowSetupNewModelView({ deal }: { deal: DealDetailDto }) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission(ESCROW_PERMISSIONS.Read);
  // The draft is refused on a Closed, Declined or Withdrawn deal
  // (EscrowTransactionService.DraftAsync; owner ruling, 2026-10-14).
  const canDraft = hasPermission(ESCROW_PERMISSIONS.Draft) && !isTerminalStage(deal.stage);
  const [createOpen, setCreateOpen] = useState(false);

  const { isLoading, transaction } = useDealEscrowTransaction(
    canRead ? deal.id : undefined,
  );

  return (
    <Card>
      <header className="mb-space-3">
        <h3 className="font-display text-h4 text-onyx m-0">
          Section 2 — Escrow Setup
        </h3>
        <p className="text-caption text-gray-mid m-0">
          Backed by the deal&apos;s linked Escrow.com transaction — link,
          amount, and status track it live. Completes automatically once the
          buyer funds it.
        </p>
      </header>

      {!canRead ? (
        <p className="text-small text-gray-mid m-0">
          You don&apos;t have permission to view escrow transactions.
        </p>
      ) : isLoading ? (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      ) : transaction ? (
        <div className="flex flex-col gap-space-4">
          <EscrowLifecycleIndicator lifecycle={transaction.lifecycle} />
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-space-3 m-0">
            <Field label="Status">
              <Badge status={escrowStatusBadge(transaction.status)}>
                {ESCROW_STATUS_LABEL[transaction.status]}
              </Badge>
            </Field>
            <Field label="Amount">
              {formatCurrency(transaction.amount)} {transaction.currency}
            </Field>
            <Field label="Escrow.com link">
              {transaction.externalUrl ? (
                <a
                  href={transaction.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold-shadow underline"
                >
                  Open escrow.com
                </a>
              ) : (
                <span className="text-gray-mid">Not yet sent to Escrow.com</span>
              )}
            </Field>
            <Field label="Description">{transaction.itemDescription}</Field>
          </dl>
          <Link
            to={`/escrow/${transaction.id}`}
            className="text-caption text-gold-shadow hover:text-gold-highlight self-start"
          >
            View full transaction
          </Link>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-space-2">
          <p className="text-small text-gray-mid m-0">
            No escrow transaction linked to this deal yet.
          </p>
          {canDraft && (
            <Button
              size="sm"
              disabled={!deal.borrowerPartyId}
              title={
                deal.borrowerPartyId
                  ? undefined
                  : "Set the deal's buyer (borrower) with an email before creating an escrow transaction."
              }
              onClick={() => setCreateOpen(true)}
            >
              Create Escrow
            </Button>
          )}
        </div>
      )}

      <BypassEscrowGateButton dealId={deal.id} />

      <CreateEscrowModal
        open={createOpen}
        deal={createOpen ? deal : null}
        onClose={() => setCreateOpen(false)}
      />
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="text-small text-onyx m-0 mt-space-1">{children}</dd>
    </div>
  );
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}
